import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  IonToolbar, IonTitle, IonButton, IonButtons, IonIcon, IonItem,
  IonLabel, IonInput,
  IonDatetime, IonModal, IonText, IonChip, IonSpinner,
  ModalController
} from '@ionic/angular/standalone';
import { NgClass, DatePipe } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { format, parse, addDays, getDay, differenceInCalendarDays, getMonth } from 'date-fns';
import { finalize, switchMap, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../services/notification.service';
import { LoaderService } from '../../../../services/loader.service';
import {
  StudenteCittadinoDettaglioService,
  ServizioMensaEventoCalendarioDTO, ServizioMensaEventoCalendarioForm, ServizioMensaEventoCalendarioType
} from '../../../../core/api/generated';
import { Studente } from '../../../interfaces/studente-figlio.interface';
import { ServiziAttiviService } from '../../../../services/pratiche/servizi-attivi.service';
import { CommandAction } from '../../../interfaces/command-action.interface';
import { UnsubscribeOnDestroy } from '../../../abstracts/unsubscribe-on-destroy';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChipState, getExcludedDays, isExcludedDay, getEventState, isChiusura, getSospesoKey, loadEventiEChiusure$
} from '../../../services/command-parser/calendar-validators';

export interface QuickChip {
  id: 'today' | 'tomorrow' | 'nextWorkday' | 'custom';
  label: string;
  date: string; // yyyy-MM-dd (empty for custom)
  shortDate: string; // e.g. "3 apr" (empty for custom)
  fullDateLabel: string;
  state: ChipState;
  stateLabel?: string;
}

@Component({
  selector: 'app-calendar-action-form',
  templateUrl: './calendar-action-form.component.html',
  styleUrls: ['./calendar-action-form.component.scss'],
  standalone: true,
  imports: [
    IonToolbar, IonTitle, IonButton, IonButtons, IonIcon, IonItem,
    IonLabel, IonInput,
    IonDatetime, IonModal, IonText, IonChip, IonSpinner,
    ReactiveFormsModule, NgClass, DatePipe, TranslocoModule
  ]
})
export class CalendarActionFormComponent extends UnsubscribeOnDestroy implements OnInit {
  @Input() action!: CommandAction;
  @Input() studente!: Studente;
  @Input() prefilledDateInizio: string | null = null;
  @Input() prefilledDateFine: string | null = null;

  /**
   * Emesso al salvataggio riuscito con le date effettive dell'evento (inizio + eventuale fine
   * del range). Il chiamante le usa per classificare il motivo di refresh del bus mensa
   * (`evento` vs `evento-retroattivo`), vedi `CommandPaletteComponent.classifyEventoReason`.
   */
  @Output() saved = new EventEmitter<{ inizio: Date; fine: Date | null }>();
  @Output() back = new EventEmitter<void>();

  ctrlDataInizio = new FormControl<string>('');
  ctrlDataFine = new FormControl<string>('');
  todayDate = '';

  isSospeso = false;
  showDatePicker = false;
  loadingEvents = true;
  saving = false;

  eventi: ServizioMensaEventoCalendarioDTO[] = [];
  /** Chiusure puntuali (ISO `yyyy-MM-dd`) da `getCalendarioMensa`: nel mondo *2 non sono negli eventi. */
  chiusure: ReadonlySet<string> = new Set();
  dayChips: QuickChip[] = [];
  customChip!: QuickChip;
  selectedChip: QuickChip | null = null;
  excludedDays: number[] = [0, 6]; // default: dom=0, sab=6

  constructor(
    private modalController: ModalController,
    private notificationService: NotificationService,
    private loaderService: LoaderService,
    private studentiService: StudenteCittadinoDettaglioService,
    private serviziAttivi: ServiziAttiviService,
    private transloco: TranslocoService
  ) {
    super();
  }

  /** Helper: traduce una chiave dello scope `command_palette.form` con eventuali parametri. */
  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`shared.command_palette.form.${key}`, params);
  }

  ngOnInit() {
    this.todayDate = format(new Date(), 'yyyy-MM-dd');
    this.ctrlDataInizio.setValue(this.prefilledDateInizio ?? this.todayDate);
    this.ctrlDataFine.setValue(this.prefilledDateFine ?? this.todayDate);

    // If dates were pre-filled by the parser, show the date picker section
    if (this.prefilledDateInizio) {
      this.showDatePicker = true;
    }

    this.checkSospeso();
    // Il chip «Altre date» non dipende dagli eventi: va creato prima del primo rendering, perché
    // `loadEventiEChiusure` arriva a `rebuildChips` solo dopo una promise e il template lo legge
    // subito (`getChipAriaLabel(customChip)` su undefined: il TypeError più frequente in prod).
    this.customChip = this.createCustomChip();
    this.loadEventiEChiusure();
  }

  get studenteFullName(): string {
    return `${this.studente?.nome ?? ''} ${this.studente?.cognome ?? ''}`.trim();
  }

  get contextQuestion(): string {
    const key = this.action?.actionType === 'assenza' ? 'domandaAssenza' : 'domandaPasta';
    return this.t(key, { nome: this.studenteFullName });
  }

  get tornaIndietroAria(): string {
    return this.t('tornaIndietroAria');
  }

  get sospesoLabel(): string {
    return this.t('sospesoStudente');
  }

  get disponibilitaAria(): string {
    return this.t('disponibilitaAria');
  }

  get verificaDisponibilita(): string {
    return this.t('verificaDisponibilita');
  }

  get dataInizioAria(): string {
    return this.t('dataInizioAria');
  }

  get dataFineAria(): string {
    return this.t('dataFineAria');
  }

  get selezionaDataInizioAria(): string {
    return this.t('selezionaDataInizio');
  }

  get selezionaDataFineAria(): string {
    return this.t('selezionaDataFine');
  }

  get annullaLabel(): string {
    return this.t('annulla');
  }

  get confermaLabel(): string {
    return this.t('conferma');
  }

  get canSave(): boolean {
    if (this.isSospeso || this.saving) return false;
    if (!this.studente?.id) return false;
    if (!this.ctrlDataInizio.value) return false;
    if (this.action?.actionType === 'assenza') {
      if (!this.ctrlDataFine.value) return false;
    }
    return true;
  }

  get saveButtonLabel(): string {
    return this.t(this.action?.actionType === 'assenza' ? 'salvaAssenza' : 'salvaPasta');
  }

  get customChipLabel(): string {
    return this.t(this.action?.actionType === 'assenza' ? 'altreDate' : 'altroGiorno');
  }

  // --- Student ---

  private checkSospeso() {
    const sospesoKey = getSospesoKey(this.studente);
    this.isSospeso = sospesoKey !== null;
    if (sospesoKey) {
      this.notificationService.openToast({ message: this.transloco.translate(sospesoKey), color: 'warning' });
    }
    this.excludedDays = getExcludedDays(this.studente);
  }

  // --- Events loading ---

  private loadEventiEChiusure() {
    const studenteId = this.studente?.id;
    if (!studenteId) {
      this.eventi = [];
      this.rebuildChips();
      return;
    }
    this.loadingEvents = true;
    // `idServizioMensaAttivo()` non rigetta mai (degrada a `null`): nessuno spinner appeso.
    this.serviziAttivi.idServizioMensaAttivo().then(idServizio => {
      if (!idServizio) {
        this.loadingEvents = false;
        this.eventi = [];
        this.chiusure = new Set();
        this.rebuildChips();
        return;
      }
      loadEventiEChiusure$(this.studentiService, studenteId, idServizio).pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingEvents = false)
      ).subscribe({
        next: ({ eventi, chiusure }) => {
          this.eventi = eventi;
          this.chiusure = chiusure;
          this.rebuildChips();
        },
        error: () => {
          this.eventi = [];
          this.chiusure = new Set();
          this.rebuildChips();
        }
      });
    });
  }

  // --- Chips ---

  private rebuildChips() {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const nextWorkday = this.getNextWorkday(tomorrow);

    const days: QuickChip[] = [
      this.createDayChip('today', this.transloco.translate('shared.command_palette.chip.oggi'), today),
      this.createDayChip('tomorrow', this.transloco.translate('shared.command_palette.chip.domani'), tomorrow),
    ];

    if (nextWorkday.getTime() !== tomorrow.getTime()) {
      const dayKeys = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
      days.push(this.createDayChip('nextWorkday', this.t('giorniLunghi.' + dayKeys[getDay(nextWorkday)]), nextWorkday));
    }

    this.dayChips = days;
    // Stesso oggetto, solo l'etichetta: il template confronta `selectedChip === customChip`, e un
    // chip nuovo deselezionerebbe le «Altre date» scelte mentre gli eventi si caricavano.
    this.customChip.label = this.customChipLabel;

    // If selected chip is now occupied, deselect
    if (this.selectedChip && this.selectedChip.id !== 'custom') {
      const updated = days.find(c => c.id === this.selectedChip!.id);
      if (updated && updated.state !== 'libero') {
        this.selectedChip = null;
      }
    }
  }

  private createCustomChip(): QuickChip {
    return {
      id: 'custom',
      label: this.customChipLabel,
      date: '',
      shortDate: '',
      fullDateLabel: '',
      state: 'libero',
    };
  }

  private createDayChip(id: 'today' | 'tomorrow' | 'nextWorkday', label: string, date: Date): QuickChip {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dateFmt = format(date, 'dd/MM/yyyy');
    // Il locale date-fns è quello globale impostato da LanguageService.use() (come la palette).
    const shortDate = format(date, 'd MMM');
    const fullDateLabel = format(date, 'd MMMM yyyy');

    const excluded = isExcludedDay(date, this.excludedDays);
    const state: ChipState = excluded ? 'chiusura' : getEventState(dateFmt, this.eventi);
    const stateLabel = excluded ? this.t('stato.chiusura') : this.getStateLabel(state);

    return { id, label, date: dateStr, shortDate, fullDateLabel, state, stateLabel };
  }

  private getStateLabel(state: ChipState): string | undefined {
    switch (state) {
      case 'assenza': return this.t('stato.assenza');
      case 'pasta-in-bianco': return this.t('stato.pastaInBianco');
      case 'chiusura': return this.t('stato.chiusura');
      default: return undefined;
    }
  }

  selectChip(chip: QuickChip) {
    if (chip.id !== 'custom' && chip.state !== 'libero') return;

    this.selectedChip = chip;

    if (chip.id === 'custom') {
      this.showDatePicker = true;
    } else {
      this.showDatePicker = false;
      this.ctrlDataInizio.setValue(chip.date);
      if (this.action?.actionType === 'assenza') {
        this.ctrlDataFine.setValue(chip.date);
      }
    }
  }

  isChipDisabled(chip: QuickChip): boolean {
    return chip.id !== 'custom' && chip.state !== 'libero';
  }

  getChipCssClass(chip: QuickChip): Record<string, boolean> {
    return {
      'chip-selected': chip === this.selectedChip,
      'chip-assenza': chip.state === 'assenza',
      'chip-pasta': chip.state === 'pasta-in-bianco',
      'chip-chiusura': chip.state === 'chiusura',
    };
  }

  getChipAriaLabel(chip: QuickChip): string {
    if (chip.id === 'custom') return this.t('chipAria.custom');
    const actionLabel = this.transloco.translate(this.action?.actionType === 'assenza'
      ? 'shared.command_palette.tipoEvento.assenza'
      : 'shared.command_palette.tipoEvento.pastaInBianco');
    if (chip.state !== 'libero') {
      return this.t('chipAria.presente', { label: chip.label, data: chip.fullDateLabel, stato: chip.stateLabel });
    }
    return this.t('chipAria.imposta', { azione: actionLabel, label: chip.label.toLowerCase(), data: chip.fullDateLabel });
  }

  private getNextWorkday(fromDate: Date): Date {
    // Cap a 7 iterazioni: se `excludedDays` coprisse tutta la settimana (config BE anomala) il
    // while girerebbe all'infinito bloccando il thread UI. Dopo 7 tentativi torna comunque una data.
    let date = fromDate;
    for (let i = 0; i < 7 && isExcludedDay(date, this.excludedDays); i++) {
      date = addDays(date, 1);
    }
    return date;
  }

  // --- Date auto-correction ("la data toccata comanda") ---

  /** L'utente ha modificato l'inizio: se la fine resta antecedente, la fine lo segue. */
  onStartChange() {
    this.selectedChip = this.customChip;
    this.enforceRange();
  }

  /** L'utente ha modificato la fine: non può precedere l'inizio, altrimenti torna all'inizio. */
  onEndChange() {
    this.selectedChip = this.customChip;
    this.enforceRange();
  }

  /**
   * Garantisce che la fine non sia mai antecedente all'inizio riportandola all'inizio. Valido solo
   * per l'assenza (l'unico caso con due date). `setValue` è control→view: non ri-emette `ionChange`,
   * quindi nessun loop con gli handler.
   */
  private enforceRange() {
    if (this.action?.actionType !== 'assenza') return;
    const inizio = this.ctrlDataInizio.value;
    const fine = this.ctrlDataFine.value;
    if (!inizio || !fine) return;
    const dInizio = parse(inizio, 'yyyy-MM-dd', new Date());
    const dFine = parse(fine, 'yyyy-MM-dd', new Date());
    if (differenceInCalendarDays(dFine, dInizio) < 0) {
      this.ctrlDataFine.setValue(inizio);
    }
  }

  // --- Suggerimento in linguaggio naturale ---

  /**
   * Riformula la selezione corrente in linguaggio naturale (testo neutro, mai un errore). Ramo
   * singolo (pasta in bianco, o assenza con inizio == fine): "Solo oggi/domani/dopodomani" per i
   * primi tre giorni, poi "Solo {data estesa}". Ramo intervallo (assenza con date diverse):
   * "Da … a … (N giorni)" con N inclusivo di entrambi gli estremi (giorni di calendario).
   * `format` senza `{locale}`: usa il locale globale impostato da `LanguageService`.
   */
  get dateHint(): string {
    const inizioIso = this.ctrlDataInizio.value;
    if (!inizioIso) return '';
    const dInizio = parse(inizioIso, 'yyyy-MM-dd', new Date());

    const fineIso = this.action?.actionType === 'assenza' ? this.ctrlDataFine.value : inizioIso;
    const dFine = fineIso ? parse(fineIso, 'yyyy-MM-dd', new Date()) : dInizio;

    // Ramo singolo: una sola data (pasta in bianco, o assenza con estremi coincidenti).
    if (differenceInCalendarDays(dFine, dInizio) <= 0 || !fineIso) {
      const delta = differenceInCalendarDays(dInizio, new Date());
      if (delta === 0) return this.t('hint.soloOggi');
      if (delta === 1) return this.t('hint.soloDomani');
      if (delta === 2) return this.t('hint.soloDopodomani');
      return this.t('hint.soloData', { data: format(dInizio, 'EEEE d MMMM') });
    }

    // Ramo intervallo: mese su entrambi gli estremi solo se cadono in mesi diversi.
    const inizioFmt = getMonth(dInizio) === getMonth(dFine)
      ? format(dInizio, 'EEEE d')
      : format(dInizio, 'EEEE d MMMM');
    const fineFmt = format(dFine, 'EEEE d MMMM');
    const giorni = differenceInCalendarDays(dFine, dInizio) + 1;
    return this.t(giorni === 1 ? 'hint.intervallo.one' : 'hint.intervallo.other', {
      inizio: inizioFmt,
      fine: fineFmt,
      giorni,
    });
  }

  // --- Save ---

  async save() {
    if (!this.canSave || this.saving) return;

    const idServizio = await this.serviziAttivi.idServizioMensaAttivo();
    if (!idServizio) {
      this.notificationService.openToast({ message: this.t('erroreSalvataggio'), color: 'danger' });
      return;
    }

    const studenteId = this.studente.id!;
    const dataInizioIso = this.ctrlDataInizio.value!;
    const dataInizioFmt = format(parse(dataInizioIso, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy');

    if (isChiusura(dataInizioFmt, this.chiusure)) {
      this.notificationService.openToast({
        message: this.t('chiusuraInizio'),
        color: 'warning'
      });
      return;
    }

    const dataFineFmt = this.action.actionType === 'assenza'
      ? format(parse(this.ctrlDataFine.value!, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
      : dataInizioFmt;

    if (this.action.actionType === 'assenza' && dataFineFmt !== dataInizioFmt && isChiusura(dataFineFmt, this.chiusure)) {
      this.notificationService.openToast({
        message: this.t('chiusuraFine'),
        color: 'warning'
      });
      return;
    }

    const tipoEvento = this.action.actionType === 'assenza'
      ? ServizioMensaEventoCalendarioType.ASSENZA
      : ServizioMensaEventoCalendarioType.PASTA_IN_BIANCO;

    const body: ServizioMensaEventoCalendarioForm = {
      data_inizio: dataInizioFmt,
      data_fine: dataFineFmt,
      tipo_evento: tipoEvento,
    };

    this.saving = true;
    this.loaderService.showLoaderObserve$().pipe(
      switchMap(() => this.studentiService.addEventoCalendarioStudenteCittadino({ idStudente: studenteId, gestioneMenseServizioId: idServizio, servizioMensaEventoCalendarioForm: body })),
      takeUntil(this.destroy$),
      finalize(() => {
        this.loaderService.hideLoader();
        this.saving = false;
      })
    ).subscribe({
      next: () => {
        const label = this.t(this.action.actionType === 'assenza' ? 'assenzaSalvata' : 'pastaSalvata');
        this.notificationService.openToast({ message: label, color: 'success' });
        const inizio = parse(this.ctrlDataInizio.value!, 'yyyy-MM-dd', new Date());
        const fine = this.action.actionType === 'assenza' && this.ctrlDataFine.value
          ? parse(this.ctrlDataFine.value, 'yyyy-MM-dd', new Date())
          : null;
        this.saved.emit({ inizio, fine });
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.openToast({
          message: error.error?.message || this.t('erroreSalvataggio'),
          color: 'danger'
        });
      }
    });
  }

  goBack() {
    this.back.emit();
  }

  closeDateModal() {
    this.modalController.dismiss();
  }
}
